import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import MobxWrap from '@/components/MobxWrap';
import { Switch } from 'antd';
import { toggleGlobalScroll } from '@/utils';
import CookieConsent from '@/components/Layout/CookieBot/CookieConsent';
import { feishuRobotSend } from '@/utils/feishuRobot';
import styles from './index.module.less';
import { GlobalTracker } from '@/utils/Tracker';
import DNSModal from './DNSModal';

const CookieBanner = (props) => {
  const { country, LANG } = props.area;
  const { isProd, CookieConsentInstance } = props.app;
  const CookieBannerRef = useRef(null);
  const DetailModalRef = useRef(null);
  const DNSModalRef = useRef(null);
  const [bannerDesc, setBannerDesc] = useState('');
  const { isUS, isGDPR } = CookieConsentInstance?.CookieCountry ?? {};
  const isCCPA = country.code === 'US';

  const handleCloseBanner = (e) => {
    e?.stopPropagation();
    CookieConsentInstance.closeBanner();
  };

  const handleAllowBanner = (e) => {
    e?.stopPropagation();
    CookieConsentInstance.allowBanner();
  };

  const handleDenyBanner = (e) => {
    e?.stopPropagation();
    CookieConsentInstance.denyBanner();
  };

  const handleShowDetailModal = (e) => {
    e?.stopPropagation();
    // CookieConsentInstance.hideBanner();
    CookieConsentInstance.showDetailModal();
  };

  useEffect(() => {
    const descKeyMap = {
      US: 'common.cookieConsent.banner.descUS',
      default: 'common.cookieConsent.banner.desc',
    };
    setBannerDesc(CookieConsentInstance?.handleLocaleReplace?.(isUS ? descKeyMap.US : descKeyMap.default));
  }, [isUS]);

  useEffect(() => {
    const loadSDK = () => {
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.id = 'Cookiebot';
        s.src = `https://consent.cookiebot.com/uc.js`;
        s.dataset.cbid = '473c7ca6-3e04-4b2e-b4c7-f3c2b802bb90';

        s.async = !0;
        s.onload = () => {
          let counter = 0;
          window.Cookiebot.removeCookiesCache = window.Cookiebot.removeCookies;

          // 触发移除cookie的场景，consent renew version之后，即本地版本和服务器版本不一致的情况
          // us.js会重新触发加载cc.js脚本，获取whitelist，并移除非白名单外的cookie
          window.Cookiebot.removeCookies = () => {
            document.cookie = `CookieConsent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            document.cookie = `CookieConsentIns=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            const timer = setInterval(() => {
              counter++;

              // 白名单列表记录的是necessary的cookie
              const whitelist = window.Cookiebot.whitelist ?? [];

              // 如果触发了删除操作，且白名单列表存在则执行cookiebot删除方法
              if (whitelist.length) {
                window.Cookiebot.removeCookiesCache();
                clearInterval(timer);
                return;
              }

              // 如果触发了删除操作，且白名单列表不存在（cookiebot 后台未配置域名），仅删除cookieconsent记录，不执行cookiebot removeCookie方法，以避免把本地所有的cookie都删除。
              if (!whitelist.length) {
                if (counter >= 50) {
                  clearInterval(timer);
                }
              }
            }, 100);
          };

          resolve();
        };
        s.onerror = () => {
          reject({ msg: 'Cookiebot加载失败', sendRobotMsg: false });
        };
        document.head.appendChild(s);
      });
    };

    const getCookiebotUserCountry = () => {
      return new Promise((resolve, reject) => {
        let counter = 0;
        const timer = setInterval(() => {
          if (window.Cookiebot?.userCountry) {
            window.Cookiebot.userCountry = window.Cookiebot.userCountry ?? country.code;
            resolve(window.Cookiebot);
            clearInterval(timer);
          }
          if (counter >= 100) {
            reject({ msg: '获取cookiebot.UserCountry加载失败', sendRobotMsg: false });
            clearInterval(timer);
          }
          counter++;
        }, 100);
      });
    };

    const initInstance = (Cookiebot = null) => {
      const instance = new CookieConsent({
        Cookiebot,
        LANG,
        isProd,
        CookieBannerRef,
        DetailModalRef,
        DNSModalRef,
        MobxApp: props.app,
        MobxAuth: props.auth,
      });
      props.app.setGlobalValue('CookieConsentInstance', instance);
      window.addEventListener('toggleGlobalScroll', (customEvent) => {
        // 如果banner显示，并且滚动条隐藏的状态下，加上滚动条偏移
        if (instance.bannerVisible) {
          if (!instance.CookieBannerRef || !instance.CookieBannerRef.current) return;
          instance.CookieBannerRef.current.style.paddingRight = `${customEvent.detail.scrollBarWidth}px`;
        }
      });
    };

    loadSDK()
      .then(async () => {
        await getCookiebotUserCountry();
        initInstance(window.Cookiebot);
      })
      .catch((e) => {
        initInstance();
        if (e?.sendRobotMsg) {
          feishuRobotSend({ text: [e.msg], err: e });
        }
      });
  }, []);

  const handleCookieBannerClickListener = (e) => {
    if (e?.target?.dataset?.clickAnchor === 'privacy-policy') {
      GlobalTracker.CookieBot({
        action: 'cookie_banner_Ppolicy',
      });
    }

    if (e?.target?.dataset?.clickAnchor === 'cookie-policy') {
      GlobalTracker.CookieBot({
        action: 'cookie_banner_Cpolicy',
      });
    }
  };

  return (
    <div>
      <div id='CookieConsentBanner' ref={CookieBannerRef} className={styles.CookieBanner}>
        <div className={styles.container} onClick={handleCookieBannerClickListener}>
          <div className={styles.info}>
            <p className={styles.title}>{LANG['common.cookieConsent.banner.title']}</p>
            <p className={styles.desc} dangerouslySetInnerHTML={{ __html: bannerDesc }} />
          </div>
          <div className={styles.btns}>
            <button className={styles.allow} onClick={handleAllowBanner}>
              {LANG['common.cookieConsent.banner.btnAllow']}
            </button>
            {!isUS && (
              <button className={styles.deny} onClick={handleDenyBanner}>
                {LANG['common.cookieConsent.btnDeny']}
              </button>
            )}
            <button className={styles.setting} onClick={handleShowDetailModal} data-thumb-sm={isUS}>
              <span>{LANG['common.cookieConsent.banner.btnSetting']}</span>
            </button>
          </div>
          {isGDPR || isUS ? (
            <button className={styles.close} onClick={handleCloseBanner}>
              <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M5.03412 5.03412C5.36606 4.70217 5.90425 4.70217 6.2362 5.03412L18.9654 17.7633C19.2973 18.0952 19.2973 18.6334 18.9654 18.9654C18.6334 19.2973 18.0952 19.2973 17.7633 18.9654L5.03412 6.2362C4.70217 5.90425 4.70217 5.36606 5.03412 5.03412Z'
                  fill='#B6B6C0'
                />
                <path
                  d='M18.9659 5.03412C18.6339 4.70217 18.0957 4.70217 17.7638 5.03412L5.03464 17.7633C4.70269 18.0952 4.70269 18.6334 5.03464 18.9654C5.36658 19.2973 5.90477 19.2973 6.23672 18.9654L18.9659 6.2362C19.2978 5.90425 19.2978 5.36606 18.9659 5.03412Z'
                  fill='#B6B6C0'
                />
              </svg>
            </button>
          ) : null}
        </div>
      </div>

      <CookieDetailModal ref={DetailModalRef} />

      {isCCPA && <DNSModal modalRef={DNSModalRef} />}
    </div>
  );
};

const CookieDetailModal = MobxWrap(
  ['area', 'app'],
  // eslint-disable-next-line
  forwardRef((props, ref) => {
    const { LANG } = props.area;
    const { CookieConsentInstance, isMobile } = props.app;
    const [visible, setVisible] = useState(false);
    const [loadMore, setLoadMore] = useState(false);
    const [category, setCategory] = useState([]);
    const isUS = CookieConsentInstance?.CookieCountry?.isUS;

    const showDetailModal = () => {
      const data = [
        {
          title: LANG['common.cookieConsent.setting.categoryNecessary'],
          desc: LANG['common.cookieConsent.setting.categoryNecessaryDesc'],
          key: 'necessary',
          value: true,
        },
        {
          title: LANG['common.cookieConsent.setting.categoryPreferences'],
          desc: LANG['common.cookieConsent.setting.categoryPreferencesDesc'],
          key: CookieConsentInstance?.Category?.preferences,
          value: CookieConsentInstance?.consent?.preferences,
        },
        {
          title: LANG['common.cookieConsent.setting.categoryStatistics'],
          desc: LANG['common.cookieConsent.setting.categoryStatisticsDesc'],
          key: CookieConsentInstance?.Category?.statistics,
          value: CookieConsentInstance?.consent?.statistics,
        },
        {
          title: LANG['common.cookieConsent.setting.categoryMarketing'],
          desc: LANG['common.cookieConsent.setting.categoryMarketingDesc'],
          key: CookieConsentInstance?.Category?.marketing,
          value: CookieConsentInstance?.consent?.marketing,
        },
      ];
      setCategory(data);
      setVisible(true);
      setLoadMore(false);
      toggleGlobalScroll(true);
    };

    const closeDetailModal = (e) => {
      e?.stopPropagation();
      setVisible(false);
      toggleGlobalScroll(false);
    };

    const loadMoreDesc = () => {
      setLoadMore(true);
    };

    const saveDetailSetting = () => {
      const optPreferences = category.find((o) => o.key === CookieConsentInstance.Category.preferences).value;
      const optStatistics = category.find((o) => o.key === CookieConsentInstance.Category.statistics).value;
      const optMarketing = category.find((o) => o.key === CookieConsentInstance.Category.marketing).value;
      CookieConsentInstance.handleSubmitCustomConsent(optPreferences, optStatistics, optMarketing, true);
      CookieConsentInstance.hideBanner();
      closeDetailModal();
      GlobalTracker.CookieBot({
        action: 'cookie_setting_save',
      });
    };

    const switchSetting = (value, item, e) => {
      e?.stopPropagation();
      category.forEach((o) => {
        if (o.key === item.key) o.value = value;
      });
      setCategory([...category]);
    };

    const toggleSetting = (item) => {
      category.forEach((o) => {
        if (o.key === item.key) o.open = !o.open;
      });
      setCategory([...category]);
    };

    // const denyDetailSetting = () => {
    //   CookieConsentInstance.handleSubmitCustomConsent(false, false, false);
    //   CookieConsentInstance.hideBanner();
    //   closeDetailModal();
    // };
    //
    // const allowDetailSetting = () => {
    //   CookieConsentInstance.handleSubmitCustomConsent(true, true, true);
    //   CookieConsentInstance.hideBanner();
    //   closeDetailModal();
    // };

    useEffect(() => {
      if (isMobile) return null;
      const eleOuter = document?.querySelector(`.${styles.middle}`);
      const scrollBarWidth = eleOuter?.offsetWidth - eleOuter?.clientWidth;
      let initWidth = 15;
      if (navigator?.platform?.indexOf('Win') !== -1) {
        initWidth = 17;
      }
      if (eleOuter) {
        eleOuter.style.padding = `0 ${initWidth - scrollBarWidth}px 0 40px`;
        eleOuter.style.margin = `24px 40px 0 0`;
      }
    }, [loadMore, category]);

    useImperativeHandle(ref, () => ({
      showDetailModal,
      closeDetailModal,
    }));

    return (
      <div id='CookieConsentDetail' className={styles.CookieDetail} data-visible={visible}>
        <div className={styles.container}>
          <div className={styles.top}>
            <span className={styles.title}>{LANG['common.cookieConsent.setting.title']}</span>
            <button className={styles.close} onClick={closeDetailModal}>
              <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M5.03412 5.03412C5.36606 4.70217 5.90425 4.70217 6.2362 5.03412L18.9654 17.7633C19.2973 18.0952 19.2973 18.6334 18.9654 18.9654C18.6334 19.2973 18.0952 19.2973 17.7633 18.9654L5.03412 6.2362C4.70217 5.90425 4.70217 5.36606 5.03412 5.03412Z'
                  fill='#B6B6C0'
                />
                <path
                  d='M18.9659 5.03412C18.6339 4.70217 18.0957 4.70217 17.7638 5.03412L5.03464 17.7633C4.70269 18.0952 4.70269 18.6334 5.03464 18.9654C5.36658 19.2973 5.90477 19.2973 6.23672 18.9654L18.9659 6.2362C19.2978 5.90425 19.2978 5.36606 18.9659 5.03412Z'
                  fill='#B6B6C0'
                />
              </svg>
            </button>
          </div>
          <div className={styles.middle} data-open={!!category.find((o) => o.open) || loadMore} data-scroll='true'>
            <div className={styles.wrapper}>
              <div className={styles.introWrap} data-hide={!loadMore} data-is-us={false}>
                <div
                  className={styles.intro}
                  dangerouslySetInnerHTML={{
                    __html: CookieConsentInstance?.handleLocaleReplace?.(
                      isUS ? 'common.cookieConsent.setting.descUS' : 'common.cookieConsent.setting.desc'
                    ),
                  }}
                />
                <button className={styles.readMore} onClick={loadMoreDesc}>
                  {LANG['common.cookieConsent.setting.btnLearnMore']}
                </button>
              </div>
              <div className={styles.category}>
                {category.map((item) => {
                  const isNecessary = item.key === 'necessary';
                  const showSwitch = !isNecessary;
                  return (
                    <div className={styles.item} data-open={item.open} key={item.key}>
                      <div className={styles.itemWrap} onClick={() => toggleSetting(item)}>
                        <div className={styles.title}>{item.title}</div>
                        <div className={styles.opts}>
                          {isNecessary && (
                            <span className={styles.activeSpan}>
                              {LANG['common.cookieConsent.setting.labelAlwaysActive']}
                            </span>
                          )}
                          {showSwitch && (
                            <Switch
                              className={styles.switch}
                              checked={item.value}
                              onChange={(value, e) => switchSetting(value, item, e)}
                            />
                          )}
                          <button className={styles.toggle}>
                            {item.open && (
                              <svg width='24' height='24' fill='none'>
                                <path fill='#fff' d='M0 0h24v24H0z' />
                                <path
                                  fill='#262629'
                                  d='M18.8571 13.2857H5.14286C4.51168 13.2857 4 12.7741 4 12.1429S4.51167 11 5.14286 11H18.8571C19.4883 11 20 11.5117 20 12.1429s-.5117 1.1428-1.1429 1.1428Z'
                                />
                              </svg>
                            )}
                            {!item.open && (
                              <svg width='24' height='24' fill='none'>
                                <path fill='#fff' d='M0 0h24v24H0z' />
                                <path
                                  fill='#262629'
                                  fillRule='evenodd'
                                  d='M10.8571 18.8571C10.8571 19.4883 11.3688 20 12 20s1.1429-.5117 1.1429-1.1429v-5.7142h5.7142C19.4883 13.1429 20 12.6312 20 12s-.5117-1.1429-1.1429-1.1429h-5.7142V5.14286C13.1429 4.51167 12.6312 4 12 4s-1.1429.51167-1.1429 1.14286v5.71424H5.14286C4.51167 10.8571 4 11.3688 4 12s.51168 1.1429 1.14286 1.1429h5.71424v5.7142Z'
                                  clipRule='evenodd'
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className={styles.contentWrap}>{item.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={styles.bottom} data-is-us={false}>
            <button className={styles.save} onClick={saveDetailSetting}>
              {LANG['common.cookieConsent.setting.btnSave']}
            </button>
            {/* {isUS && ( */}
            {/*   <> */}
            {/*     <button className={styles.deny} onClick={denyDetailSetting}> */}
            {/*       {LANG['common.cookieConsent.setting.btnDNSOS']} */}
            {/*     </button> */}
            {/*     <button className={styles.allow} onClick={allowDetailSetting}> */}
            {/*       {LANG['common.cookieConsent.setting.btnAllowAll']} */}
            {/*     </button> */}
            {/*   </> */}
            {/* )} */}
          </div>
        </div>
      </div>
    );
  })
);

export default MobxWrap(['area', 'app', 'auth'], CookieBanner);
